/**
 * Scheda tecnica di un progetto e modifiche richieste (ADR 0048).
 *
 * Tre servizi, un solo client per la pagina del progetto:
 *
 *  - `projects` tiene la scheda (`GET /api/v1/projects/{id}/profile`) e annulla una
 *    richiesta (`POST …/change-requests/{rid}/cancel`);
 *  - `agents` consegna una richiesta all'Institore del set che ospita il progetto
 *    (`POST /api/v1/agent-sets/{set}/projects/{id}/change-requests`);
 *  - `tools` dice se c'è il bucket del progetto e quanto è pieno
 *    (`GET /api/v1/tools/projects/{id}/object-storage`).
 *
 * Le sezioni le scrivono gli agenti con uno schema chiuso: i tipi qui sotto sono quelli di
 * `services/projects/src/projects/domain/profile_*.py`. Una sezione assente vuol dire «non
 * dichiarata», non «vuota».
 *
 * Provvisorio dichiarato, come gli altri client (CLAUDE.md §2.3): tipi scritti a mano.
 */

import type { ApiClient } from './client';
import type { SetMessage } from './agentTypes';

/** Sezioni della scheda. */
export type ProfileSectionName =
  | 'auth'
  | 'database'
  | 'storage'
  | 'stack'
  | 'integrations'
  | 'mobile'
  | 'origin'
  | 'overview';

/** Un metodo di accesso. */
export interface AuthMethod {
  /** `email_password`, `magic_link`, `oauth`, `email_code`, `sms_code`, `totp`, `passkey`, `api_key`, `other`. */
  kind: string;
  /** Per `oauth`: `google`, `github`… */
  provider: string;
  /** Nota. */
  notes: string;
}

/** Sezione `auth`. */
export interface AuthSection {
  /** Metodi; vuoto = nessun accesso degli utenti. */
  methods: AuthMethod[];
  /** Sessione. */
  session: {
    /** `cookie_jwt`, `cookie_session`, `bearer_token`, `other`. */
    mechanism: string | null;
    /** Durata massima in secondi. */
    duration_seconds: number | null;
    /** Uscita dopo inattività, in secondi. */
    idle_timeout_seconds: number | null;
    /** `none`, `sliding`, `refresh_token`, `other`. */
    renewal: string | null;
  };
  /** Regole delle password; `null` se non ci sono password. */
  password: {
    /** Lunghezza minima. */
    min_length: number | null;
    /** Lunghezza massima. */
    max_length: number | null;
    /** `lower`, `upper`, `digit`, `symbol`. */
    required_characters: string[];
    /** Algoritmo. */
    hash: string | null;
    /** True se il cambio chiede la password attuale. */
    change_requires_current: boolean | null;
    /** Recupero. */
    reset: string | null;
    /** Scadenza in giorni. */
    expiry_days: number | null;
  } | null;
  /** Blocco dei tentativi; `null` se non c'è. */
  lockout: {
    /** Tentativi nella finestra. */
    max_attempts: number | null;
    /** Finestra in secondi. */
    window_seconds: number | null;
    /** Chiave del conteggio. */
    scope: string | null;
    /** False se il conteggio si azzera al riavvio. */
    persistent: boolean | null;
  } | null;
  /** Conferma dell'indirizzo. */
  email_verification: boolean | null;
  /** `none`, `optional`, `required`. */
  two_factor: string | null;
  /** `open`, `invite_only`, `closed`, `other`. */
  registration: string | null;
  /** Note. */
  notes: string;
}

/** Un database. */
export interface DatabaseEntry {
  /** Motore. */
  engine: string;
  /** Versione. */
  version: string;
  /** `site_namespace`, `external`, `embedded`, `other`. */
  location: string;
  /** Host per `external`. */
  host: string;
  /** Fornitore. */
  provider: string;
  /** Dove stanno i dati. */
  storage: string;
  /** A che cosa serve. */
  purpose: string;
}

/** Sezione `database`. */
export interface DatabaseSection {
  /** Database; vuoto = nessuno. */
  databases: DatabaseEntry[];
  /** Note. */
  notes: string;
}

/** Sezione `storage`. */
export interface StorageSection {
  /** `none`, `platform_r2`, `external_s3`, `volume`, `other`. */
  kind: string;
  /** Fornitore. */
  provider: string;
  /** Che cosa ci finisce. */
  usage: string;
  /** True con caricamenti diretti dal browser. */
  direct_uploads: boolean | null;
  /** Note. */
  notes: string;
}

/** Una tecnologia. */
export interface Tech {
  /** Nome. */
  name: string;
  /** Versione. */
  version: string;
}

/** Sezione `stack`. */
export interface StackSection {
  /** Linguaggi. */
  languages: Tech[];
  /** Interfaccia web. */
  frontend: Tech[];
  /** Server. */
  backend: Tech[];
  /** App mobile. */
  mobile: Tech[];
  /** Dove gira. */
  runtime: Tech[];
  /** Librerie principali. */
  libraries: Tech[];
  /** Strumenti di test. */
  tests: Tech[];
  /** Note. */
  notes: string;
}

/** Sezione `integrations`. */
export interface IntegrationsSection {
  /** Servizi esterni. */
  items: { kind: string; provider: string; notes: string }[];
  /** Note. */
  notes: string;
}

/** Sezione `mobile`. */
export interface MobileSection {
  /** `android`, `ios`. */
  platforms: string[];
  /** `expo`, `react_native`, `flutter`, `native`, `other`. */
  framework: string | null;
  /** Note. */
  notes: string;
}

/** Sezione `origin`. */
export interface OriginSection {
  /** Template del catalogo; vuoto se nessuno. */
  template: string;
  /** Note. */
  notes: string;
}

/** Una domanda frequente della panoramica. */
export interface OverviewFaq {
  /** Domanda. */
  question: string;
  /** Risposta breve. */
  answer: string;
}

/**
 * Sezione `overview`: il progetto in parole semplici, scritto dall'orchestratore. La legge
 * anche la voce di institor (ADR 0028 §3.3).
 */
export interface OverviewSection {
  /** Che cosa fa l'app e per chi. */
  summary: string;
  /** Com'è fatta, in parole semplici. */
  structure: string;
  /** Limiti noti. */
  known_limits: string;
  /** Domande frequenti. */
  faq: OverviewFaq[];
  /** Note. */
  notes: string;
}

/** Contenuto di ogni sezione. */
export interface ProfileSectionData {
  auth: AuthSection;
  database: DatabaseSection;
  storage: StorageSection;
  stack: StackSection;
  integrations: IntegrationsSection;
  mobile: MobileSection;
  origin: OriginSection;
  overview: OverviewSection;
}

/** Una sezione dichiarata, con la provenienza. */
export interface DeclaredSection<K extends ProfileSectionName = ProfileSectionName> {
  /** Nome. */
  section: K;
  /** Versione della forma. */
  schema_version: number;
  /** Contenuto. */
  data: ProfileSectionData[K];
  /** Ruolo dell'agente che l'ha scritta. */
  updated_by: string;
  /** Set di provenienza. */
  agent_set_id: string | null;
  /** Commit descritto; vuoto se non dichiarato. */
  commit_sha: string;
  /** Ultima scrittura, ISO 8601. */
  updated_at: string;
}

/**
 * Argomento di una modifica. `release` (scelta `production`) chiede un rilascio in
 * produzione, `requirements` (scelta `sync`) chiede all'Institore di allineare il codice ai
 * requisiti funzionali (ADR 0050 §4.2, §6.2).
 */
export type ChangeTopic = 'storage' | 'database' | 'auth' | 'profile' | 'release' | 'requirements';

/** Stato di una modifica. */
export type ChangeState = 'requested' | 'delivered' | 'in_progress' | 'applied' | 'rejected' | 'cancelled';

/** Stati di una richiesta aperta. */
export const OPEN_CHANGE_STATES: readonly ChangeState[] = ['requested', 'delivered', 'in_progress'];

/** Una modifica richiesta. */
export interface ChangeRequest {
  /** Identificativo. */
  id: string;
  /** Argomento. */
  topic: ChangeTopic;
  /** Scelta. */
  target: string;
  /** Dettagli pubblici. */
  details: Record<string, unknown>;
  /** Nota dell'utente. */
  note: string;
  /** Stato. */
  state: ChangeState;
  /** Avanzamento o esito dell'Institore, o motivo dell'annullamento. */
  outcome: string;
  /** Etichetta di chi l'ha chiesta. */
  requested_by: string;
  /** Nome leggibile di chi l'ha chiesta; vuoto se non noto. */
  requested_by_name: string;
  /** Set a cui è stata consegnata. */
  agent_set_id: string | null;
  /** Messaggio di consegna. */
  message_id: string | null;
  /** Istante della richiesta. */
  created_at: string;
  /** Ultimo cambio. */
  updated_at: string;
  /** Chiusura. */
  closed_at: string | null;
}

/** Scheda di un progetto. */
export interface ProjectProfile {
  /** Progetto. */
  project_id: string;
  /** Versione della forma delle sezioni. */
  schema_version: number;
  /** Sezioni dichiarate, per nome. */
  sections: Partial<{ [K in ProfileSectionName]: DeclaredSection<K> }>;
  /** Richieste aperte e ultime chiuse, dalla più recente. */
  change_requests: ChangeRequest[];
  /** Scelte predefinite (`storage`, `database`, `versioning`). */
  defaults: Record<string, string>;
  /** Nome leggibile del creatore; vuoto se non noto. */
  created_by_name: string;
}

/** Corpo di una richiesta di modifica. */
export interface NewChangeRequest {
  /** Argomento. */
  topic: ChangeTopic;
  /** Scelta. */
  target: string;
  /** Dettagli pubblici, mai credenziali. */
  details?: Record<string, unknown>;
  /** Nota per l'Institore. */
  note?: string;
}

/** Esito della consegna. */
export interface DeliveredChange {
  /** Richiesta. */
  change_request: ChangeRequest;
  /** Messaggio in coda al relay. */
  message: SetMessage;
}

/** Bucket del progetto (senza endpoint né credenziali). */
export interface ProjectBucket {
  /** Fornitore (`r2`). */
  provider: string;
  /** Nome (`p-<id>`). */
  bucket: string;
  /** Giurisdizione o regione. */
  region: string;
  /** `ready`, `failed`, `deleting`. */
  state: string;
  /** Byte usati all'ultima misura. */
  usage_bytes: number | null;
  /** Oggetti all'ultima misura. */
  object_count: number | null;
  /** Istante dell'ultima misura. */
  usage_measured_at: string | null;
  /** Creazione. */
  created_at: string | null;
}

/** Storage a oggetti del progetto. */
export interface ProjectObjectStorage {
  /** True se lo storage della piattaforma è acceso. */
  enabled: boolean;
  /** Fornitore configurato. */
  provider: string;
  /** Bucket del progetto; `null` se non c'è. */
  bucket: ProjectBucket | null;
}

/** Scopo in `secrets` delle credenziali di uno storage o di un database dell'utente. */
export const CREDENTIAL_PURPOSES: Record<'storage' | 'database', string> = {
  storage: 'storage:external',
  database: 'database:external',
};

/** Operazioni della scheda, esposte alla pagina del progetto. */
export class ProjectProfileApi {
  /**
   * @param client Client HTTP verso il gateway.
   */
  constructor(private readonly client: ApiClient) {}

  /**
   * Legge la scheda di un progetto.
   *
   * @param projectId Progetto.
   * @returns Sezioni dichiarate e modifiche richieste.
   * @throws ApiError 403 senza lettura sul progetto.
   */
  get(projectId: string): Promise<ProjectProfile> {
    return this.client.request<ProjectProfile>('GET', `/api/v1/projects/${encodeURIComponent(projectId)}/profile`);
  }

  /**
   * Chiede una modifica all'Institore del set che ospita il progetto.
   *
   * @param setId Set.
   * @param projectId Progetto.
   * @param request Argomento, scelta, dettagli e nota.
   * @returns Richiesta e messaggio.
   * @throws ApiError 409 `read_only_project`, `profile_conflict`, `queue_full`; 422 `profile_invalid`.
   */
  requestChange(setId: string, projectId: string, request: NewChangeRequest): Promise<DeliveredChange> {
    return this.client.request<DeliveredChange>(
      'POST',
      `/api/v1/agent-sets/${encodeURIComponent(setId)}/projects/${encodeURIComponent(projectId)}/change-requests`,
      request,
    );
  }

  /**
   * Annulla una richiesta ancora aperta.
   *
   * @param projectId Progetto.
   * @param requestId Richiesta.
   * @returns La richiesta annullata.
   */
  cancel(projectId: string, requestId: string): Promise<ChangeRequest> {
    return this.client.request<ChangeRequest>(
      'POST',
      `/api/v1/projects/${encodeURIComponent(projectId)}/change-requests/${encodeURIComponent(requestId)}/cancel`,
    );
  }

  /**
   * Legge lo storage a oggetti del progetto.
   *
   * @param projectId Progetto.
   * @returns Acceso o spento, e il bucket se c'è.
   */
  objectStorage(projectId: string): Promise<ProjectObjectStorage> {
    return this.client.request<ProjectObjectStorage>(
      'GET',
      `/api/v1/tools/projects/${encodeURIComponent(projectId)}/object-storage`,
    );
  }
}
